import { POS_DEVICE_CLIENT_ID } from "@tovi/shared";
import { baseUrl } from "./api.ts";

/**
 * The till pairing itself in (`02-architecture.md` § Auth, RFC 8628).
 *
 * Framework-free like `till.ts`, so it can be exercised with `bun test` and no
 * DOM. `sign-in.tsx` is the only caller and the only thing that touches React.
 */

export type PairingCode = {
  readonly deviceCode: string;
  readonly userCode: string;
  readonly verificationUri: string;
  readonly verificationUriComplete: string;
  /** Wall-clock time (`Date.now()`), not a duration — compared against on every poll. */
  readonly expiresAt: number;
  readonly intervalMs: number;
};

export async function requestPairingCode(signal: AbortSignal): Promise<PairingCode> {
  const response = await fetch(`${baseUrl()}/api/auth/device/code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ client_id: POS_DEVICE_CLIENT_ID }),
    signal,
  });
  if (!response.ok) throw new Error("could not request a pairing code");

  const body = (await response.json()) as {
    device_code: string;
    user_code: string;
    verification_uri: string;
    verification_uri_complete: string;
    expires_in: number;
    interval: number;
  };
  return {
    deviceCode: body.device_code,
    userCode: body.user_code,
    verificationUri: body.verification_uri,
    verificationUriComplete: body.verification_uri_complete,
    expiresAt: Date.now() + body.expires_in * 1000,
    intervalMs: body.interval * 1000,
  };
}

export type PairingOutcome =
  | { kind: "approved"; token: string }
  | { kind: "expired" }
  | { kind: "denied" }
  | { kind: "failed" };

type Sleep = (ms: number, signal: AbortSignal) => Promise<void>;

function realSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

/**
 * Never settles. Reaching this means `signal` aborted — the screen unmounted
 * or switched to the password form — and whoever called `waitForApproval` has
 * already stopped listening for an answer. A poll must not run again once it
 * has, and there is no outcome left to report to nobody.
 */
function abandoned(): Promise<PairingOutcome> {
  return new Promise(() => {});
}

/**
 * Polls `POST /device/token` until the code is approved, denied, expires, or
 * fails outright.
 *
 * `onRetrying`, when given, is told `true` the moment a poll throws — a
 * network blip, not a refusal — and `false` the next time one actually
 * reaches the server, so a screen can say it is retrying without that being
 * one of this function's terminal outcomes: the loop does not stop for it.
 *
 * `sleep` exists to be replaced in a test; the default is a real timer.
 */
export async function waitForApproval(
  code: PairingCode,
  signal: AbortSignal,
  onRetrying?: (retrying: boolean) => void,
  sleep: Sleep = realSleep,
): Promise<PairingOutcome> {
  let intervalMs = code.intervalMs;

  while (!signal.aborted) {
    if (Date.now() >= code.expiresAt) return { kind: "expired" };

    let response: Response;
    try {
      response = await fetch(`${baseUrl()}/api/auth/device/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grant_type: "urn:ietf:params:oauth:grant-type:device_code",
          device_code: code.deviceCode,
          client_id: POS_DEVICE_CLIENT_ID,
        }),
        signal,
      });
    } catch {
      onRetrying?.(true);
      await sleep(intervalMs, signal);
      continue;
    }
    onRetrying?.(false);

    if (response.ok) {
      const body = (await response.json()) as { access_token: string };
      return { kind: "approved", token: body.access_token };
    }

    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    switch (body?.error) {
      case "authorization_pending":
        break;
      case "slow_down":
        // RFC 8628 § 3.5: the plugin refuses a poll sooner than the interval
        // it stored, so this has to hold for the rest of this code, not just
        // the next wait.
        intervalMs += 5000;
        break;
      case "expired_token":
        return { kind: "expired" };
      case "access_denied":
        return { kind: "denied" };
      default:
        return { kind: "failed" };
    }

    await sleep(intervalMs, signal);
  }

  return abandoned();
}
